import { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Icon from "./Icon";

const CACHE_KEY = (uid) => `gulliver_stats_${uid}`;
const CACHE_VERSION = 5;

const AIRPORT_COUNTRIES = {
  JFK: "USA", LAX: "USA", ORD: "USA", ATL: "USA", DFW: "USA", MIA: "USA",
  SFO: "USA", SEA: "USA", BOS: "USA", DEN: "USA", LAS: "USA", PHX: "USA",
  CMH: "USA", CLT: "USA", MSP: "USA", DTW: "USA", PHL: "USA", LGA: "USA",
  CDG: "France", LHR: "UK", AMS: "Netherlands", FRA: "Germany", MAD: "Spain",
  FCO: "Italy", ZRH: "Switzerland", BCN: "Spain", MUC: "Germany", VIE: "Austria",
  LIS: "Portugal", CPH: "Denmark", ARN: "Sweden", HEL: "Finland", OSL: "Norway",
  NRT: "Japan", HND: "Japan", PEK: "China", PVG: "China", HKG: "Hong Kong",
  SIN: "Singapore", BKK: "Thailand", SYD: "Australia", MEL: "Australia",
  DXB: "UAE", DOH: "Qatar", AUH: "UAE", IST: "Turkey", TLV: "Israel",
  GRU: "Brazil", EZE: "Argentina", BOG: "Colombia", LIM: "Peru",
  YYZ: "Canada", YVR: "Canada", YUL: "Canada",
  JNB: "South Africa", CAI: "Egypt", NBO: "Kenya",
  MRS: "France", NCE: "France", LYS: "France",
  MXP: "Italy", VCE: "Italy", FLR: "Italy", NAP: "Italy",
  MDW: "USA",
};


// Canonical country lookup — maps any variant to a canonical name
const COUNTRY_LOOKUP = {};
[
  [["USA","US","U.S.","U.S.A.","United States","United States of America"], "USA"],
  [["UK","U.K.","United Kingdom","Great Britain","England","Britain"], "UK"],
  [["France","FR","France"], "France"],
  [["Germany","DE","Deutschland","Allemagne"], "Germany"],
  [["Italy","IT","Italia","Italie"], "Italy"],
  [["Spain","ES","España","Espagne"], "Spain"],
  [["Netherlands","NL","Nederland","Pays-Bas","Holland"], "Netherlands"],
  [["Switzerland","CH","Schweiz","Suisse"], "Switzerland"],
  [["Austria","AT","Österreich"], "Austria"],
  [["Portugal","PT"], "Portugal"],
  [["Belgium","BE","Belgique","België"], "Belgium"],
  [["Denmark","DK","Danmark"], "Denmark"],
  [["Sweden","SE","Sverige"], "Sweden"],
  [["Norway","NO","Norge"], "Norway"],
  [["Finland","FI","Suomi"], "Finland"],
  [["Poland","PL","Polska"], "Poland"],
  [["Czech Republic","CZ","Czechia"], "Czech Republic"],
  [["Hungary","HU","Magyarország"], "Hungary"],
  [["Greece","GR","Grèce","Hellas"], "Greece"],
  [["Croatia","HR","Hrvatska"], "Croatia"],
  [["Ireland","IE","Éire"], "Ireland"],
  [["Scotland"], "Scotland"],
  [["Japan","JP","Japon","Nippon"], "Japan"],
  [["China","CN","Chine","Zhongguo"], "China"],
  [["Hong Kong","HK"], "Hong Kong"],
  [["Singapore","SG"], "Singapore"],
  [["Thailand","TH","Thaïlande"], "Thailand"],
  [["Australia","AU"], "Australia"],
  [["New Zealand","NZ"], "New Zealand"],
  [["Canada","CA"], "Canada"],
  [["Mexico","MX","Mexique","México"], "Mexico"],
  [["Brazil","BR","Brésil","Brasil"], "Brazil"],
  [["Argentina","AR"], "Argentina"],
  [["Colombia","CO"], "Colombia"],
  [["Peru","PE","Pérou"], "Peru"],
  [["Chile","CL"], "Chile"],
  [["UAE","AE","United Arab Emirates"], "UAE"],
  [["Qatar","QA"], "Qatar"],
  [["Turkey","TR","Turquie","Türkiye"], "Turkey"],
  [["Israel","IL"], "Israel"],
  [["Egypt","EG","Égypte"], "Egypt"],
  [["Morocco","MA","Maroc"], "Morocco"],
  [["South Africa","ZA"], "South Africa"],
  [["Kenya","KE"], "Kenya"],
  [["South Korea","KR","Korea"], "South Korea"],
  [["Taiwan","TW"], "Taiwan"],
  [["Vietnam","VN","Viêt Nam"], "Vietnam"],
  [["Indonesia","ID"], "Indonesia"],
  [["Malaysia","MY"], "Malaysia"],
  [["Philippines","PH"], "Philippines"],
  [["India","IN","Inde"], "India"],
  [["Iceland","IS","Islande"], "Iceland"],
  [["Cuba","CU"], "Cuba"],
  [["Costa Rica","CR"], "Costa Rica"],
  [["Panama","PA"], "Panama"],
  [["Ecuador","EC"], "Ecuador"],
  [["Uruguay","UY"], "Uruguay"],
  [["Cambodia","KH"], "Cambodia"],
  [["Myanmar","MM","Burma"], "Myanmar"],
  [["Nepal","NP"], "Nepal"],
  [["Sri Lanka","LK"], "Sri Lanka"],
  [["Jordan","JO"], "Jordan"],
  [["Bahrain","BH"], "Bahrain"],
  [["Kuwait","KW"], "Kuwait"],
  [["Oman","OM"], "Oman"],
  [["Saudi Arabia","SA"], "Saudi Arabia"],
  [["Tanzania","TZ"], "Tanzania"],
  [["Ethiopia","ET"], "Ethiopia"],
  [["Ghana","GH"], "Ghana"],
  [["Nigeria","NG"], "Nigeria"],
  [["Tunisia","TN","Tunisie"], "Tunisia"],
  [["Romania","RO","România"], "Romania"],
  [["Bulgaria","BG"], "Bulgaria"],
  [["Serbia","RS"], "Serbia"],
  [["Slovakia","SK"], "Slovakia"],
  [["Slovenia","SI"], "Slovenia"],
  [["Estonia","EE"], "Estonia"],
  [["Latvia","LV"], "Latvia"],
  [["Lithuania","LT"], "Lithuania"],
  [["Luxembourg","LU"], "Luxembourg"],
  [["Malta","MT"], "Malta"],
  [["Cyprus","CY"], "Cyprus"],
  [["Georgia","GE"], "Georgia"],
  [["Armenia","AM"], "Armenia"],
  [["Azerbaijan","AZ"], "Azerbaijan"],
  [["Kazakhstan","KZ"], "Kazakhstan"],
  [["Uzbekistan","UZ"], "Uzbekistan"],
].forEach(([variants, canonical]) => {
  variants.forEach(v => { COUNTRY_LOOKUP[v.toLowerCase()] = canonical; });
});

function extractCountryFromAddress(address) {
  if (!address) return null;
  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  for (let i = parts.length - 1; i >= Math.max(0, parts.length - 3); i--) {
    const candidate = parts[i].replace(/\d+/g, '').trim();
    const match = COUNTRY_LOOKUP[candidate.toLowerCase()];
    if (match) return match;
  }
  return null;
}


// Airport coordinates for miles calculation [lat, lng]
const AIRPORT_COORDS = {
  // North America
  JFK:[40.6413,-73.7781], LAX:[33.9425,-118.4081], ORD:[41.9742,-87.9073],
  ATL:[33.6407,-84.4277], DFW:[32.8998,-97.0403], MIA:[25.7959,-80.2870],
  SFO:[37.6213,-122.3790], SEA:[47.4502,-122.3088], BOS:[42.3656,-71.0096],
  DEN:[39.8561,-104.6737], LAS:[36.0840,-115.1537], PHX:[33.4373,-112.0078],
  CMH:[39.9980,-82.8919], CLT:[35.2140,-80.9431], MSP:[44.8848,-93.2223],
  DTW:[42.2124,-83.3534], PHL:[39.8721,-75.2411], LGA:[40.7772,-73.8726],
  MDW:[41.7868,-87.7522], IAH:[29.9902,-95.3368], EWR:[40.6895,-74.1745],
  SAN:[32.7336,-117.1897], TPA:[27.9755,-82.5332], PDX:[45.5898,-122.5951],
  MSY:[29.9934,-90.2580], AUS:[30.1975,-97.6664], BNA:[36.1245,-86.6782],
  RDU:[35.8776,-78.7875], IND:[39.7173,-86.2944], PIT:[40.4915,-80.2329],
  CLE:[41.4117,-81.8498], MCI:[39.2976,-94.7139], SLC:[40.7884,-111.9778],
  OAK:[37.7213,-122.2208], SJC:[37.3626,-121.9290], STL:[38.7487,-90.3700],
  MEM:[35.0424,-89.9767], BDL:[41.9389,-72.6832], ABQ:[35.0402,-106.6090],
  YYZ:[43.6777,-79.6248], YVR:[49.1967,-123.1815], YUL:[45.4706,-73.7408],
  YYC:[51.1315,-114.0106], YEG:[53.3097,-113.5800],
  GRU:[(-23.4356),(-46.4731)], EZE:[(-34.8222),(-58.5358)],
  BOG:[4.7016,-74.1469], LIM:[(-12.0219),(-77.1143)], SCL:[(-33.3930),(-70.7858)],
  MEX:[19.4363,-99.0721], CUN:[21.0365,-86.8771], PTY:[9.0714,-79.3835],

  // Europe
  LHR:[51.4775,-0.4614], LGW:[51.1481,-0.1903], LCY:[51.5048,-0.0495],
  CDG:[49.0097,2.5479], ORY:[48.7233,2.3794],
  FRA:[50.0379,8.5622], MUC:[48.3537,11.7750], DUS:[51.2895,6.7668],
  AMS:[52.3086,4.7639], BRU:[50.9014,4.4844],
  MAD:[40.4719,-3.5626], BCN:[41.2974,2.0833],
  FCO:[41.8003,12.2389], MXP:[45.6301,8.7231], VCE:[45.5053,12.3519],
  NAP:[40.8860,14.2908], FLR:[43.8100,11.2051],
  ZRH:[47.4582,8.5555], GVA:[46.2380,6.1089],
  VIE:[48.1103,16.5697], PRG:[50.1008,14.2600], BUD:[47.4298,19.2611],
  WAW:[52.1657,20.9671], ARN:[59.6519,17.9186], CPH:[55.6179,12.6560],
  OSL:[60.1939,11.1004], HEL:[60.3172,24.9633], LIS:[38.7742,-9.1342],
  ATH:[37.9364,23.9445], IST:[41.2753,28.7519], SAW:[40.8986,29.3092],
  DUB:[53.4213,-6.2700], EDI:[55.9500,-3.3725], MAN:[53.3537,-2.2750],
  NCE:[43.6584,7.2159], MRS:[43.4393,5.2214], LYS:[45.7256,5.0811],
  BER:[52.3667,13.5033], HAM:[53.6304,9.9882], STR:[48.6899,9.2220],
  PMI:[39.5517,2.7388], AGP:[36.6749,-4.4991],

  // Asia Pacific
  NRT:[35.7647,140.3864], HND:[35.5494,139.7798], KIX:[34.4347,135.2440],
  PEK:[40.0801,116.5846], PVG:[31.1443,121.8083], CAN:[23.3924,113.2988],
  HKG:[22.3080,113.9185], TPE:[25.0777,121.2328],
  ICN:[37.4602,126.4407], GMP:[37.5583,126.7906],
  SIN:[1.3644,103.9915], KUL:[2.7456,101.7072], BKK:[13.6811,100.7473],
  CGK:[(-6.1256),106.6559], MNL:[14.5086,121.0194],
  SYD:[(-33.9399),151.1753], MEL:[(-37.6690),144.8410], BNE:[(-27.3842),153.1175],
  AKL:[(-37.0082),174.7917], CHC:[(-43.4894),172.5322],
  DEL:[28.5562,77.1000], BOM:[19.0896,72.8656], MAA:[12.9900,80.1693],
  CCU:[22.6547,88.4467], BLR:[13.1986,77.7066],
  DXB:[25.2532,55.3657], AUH:[24.4330,54.6511], DOH:[25.2609,51.6138],
  KWI:[29.2267,47.9689], BAH:[26.2708,50.6336], MCT:[23.5933,58.2844],
  TLV:[32.0114,34.8867], CAI:[30.1219,31.4056], AMM:[31.7226,35.9932],
  NBO:[(-1.3192),36.9275], JNB:[(-26.1392),28.2460], CPT:[(-33.9649),18.6017],
  ADD:[8.9779,38.7993], LOS:[6.5774,3.3212], ACC:[5.6052,-0.1668],
  CMN:[33.3675,-7.5900],

  // Middle East / Central Asia
  GYD:[40.4675,50.0467], TAS:[41.2579,69.2811],
};

function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
}

function extractAirportCodes(title) {
  const match = title?.match(/([A-Z]{3})\s*[-–>→]+\s*([A-Z]{3})/);
  if (match) return [match[1], match[2]];
  return [];
}

function getCountryFromAirport(code) {
  return AIRPORT_COUNTRIES[code] || null;
}

function tripsFingerprint(tripsSnap) {
  return tripsSnap.docs.map(d => {
    const data = d.data();
    const updated = data.updatedAt?.toMillis?.() || data.createdAt?.toMillis?.() || 0;
    return `${d.id}:${updated}`;
  }).sort().join("|");
}

async function calculateStats(user) {
  const tripsSnap = await getDocs(
    query(collection(db, "trips"), where("memberIds", "array-contains", user.uid))
  );

  const fingerprint = tripsFingerprint(tripsSnap);

  let totalFlights = 0;
  let totalMiles = 0;
  let totalHotelNights = 0;
  let totalRestaurants = 0;
  let totalTrains = 0;
  let totalActivities = 0;
  let countries = new Set();
  let cities = new Set();
  let airlines = {};
  let mostVisitedCity = {};
  let tripsByYear = {};

  for (const tripDoc of tripsSnap.docs) {
    const trip = tripDoc.data();
    const year = trip.startDate?.substring(0, 4);
    if (year) tripsByYear[year] = (tripsByYear[year] || 0) + 1;

    const segsSnap = await getDocs(collection(db, "trips", tripDoc.id, "segments"));

    for (const segDoc of segsSnap.docs) {
      const seg = segDoc.data();

      // Skip segments hidden by this user
      if ((seg.hiddenFor || []).includes(user.uid)) continue;

      if (seg.type === "flight") {
        totalFlights++;
        const codes = extractAirportCodes(seg.title);
        if (codes.length === 2) {
          const c1 = AIRPORT_COORDS[codes[0]];
          const c2 = AIRPORT_COORDS[codes[1]];
          if (c1 && c2) totalMiles += haversineMiles(c1[0], c1[1], c2[0], c2[1]);
        }
        const destCode = codes[1] || codes[0];
        if (destCode) {
          mostVisitedCity[destCode] = (mostVisitedCity[destCode] || 0) + 1;
        }
        codes.forEach(code => {
          const country = getCountryFromAirport(code);
          if (country) countries.add(country);
        });
        const airlineMatch = seg.detail?.match(/^([^·]+)/);
        if (airlineMatch) {
          let airline = airlineMatch[1].trim();
          airline = airline.replace(/\s+[A-Z0-9]{2}\s+\d{1,4}$/, "").trim();
          airline = airline.replace(/\s+[A-Z]{2}\d{1,4}$/, "").trim();
          const looksLikeRoute = /[→>–-]|^[A-Z]{3}$/.test(airline);
          if (airline && airline.length > 1 && !looksLikeRoute) {
            airlines[airline] = (airlines[airline] || 0) + 1;
          }
        }
      }

      if (seg.type === "hotel") {
        const checkin = seg.date;
        const checkout = seg.checkoutDate;
        if (checkin && checkout) {
          const nights = Math.round((new Date(checkout) - new Date(checkin)) / (1000 * 60 * 60 * 24));
          if (nights > 0) totalHotelNights += nights;
        } else {
          totalHotelNights += 1;
        }
        if (seg.address) {
          const cityPart = seg.address.split(",")[1]?.trim();
          if (cityPart) cities.add(cityPart);
          const hotelCountry = extractCountryFromAddress(seg.address);
          if (hotelCountry) countries.add(hotelCountry);
        }
      }

      if (seg.type === "restaurant") totalRestaurants++;
      if (seg.type === "train") totalTrains++;
      if (seg.type === "activity") totalActivities++;
    }
  }

  const topAirline = Object.entries(airlines).sort(([,a],[,b]) => b - a)[0]?.[0] || null;
  const topAirport = Object.entries(mostVisitedCity).sort(([,a],[,b]) => b - a)[0] || null;
  const busiestYear = Object.entries(tripsByYear).sort(([,a],[,b]) => b - a)[0] || null;

  return {
    fingerprint,
    calculatedAt: Date.now(),
    version: CACHE_VERSION,
    totalTrips: tripsSnap.size,
    totalFlights,
    totalMiles,
    milesCalculated: totalMiles > 0,
    totalHotelNights,
    totalRestaurants,
    totalTrains,
    totalActivities,
    countries: countries.size,
    countriesList: [...countries].sort(),
    cities: cities.size,
    topAirline,
    topAirport,
    busiestYear,
    tripsByYear,
  };
}

export default function StatsPage({ user, onBack }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const cached = localStorage.getItem(CACHE_KEY(user.uid));
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.version === CACHE_VERSION) {
            setStats(parsed);
            setLoading(false);
            setFromCache(true);

            const tripsSnap = await getDocs(
              query(collection(db, "trips"), where("memberIds", "array-contains", user.uid))
            );
            const currentFingerprint = tripsFingerprint(tripsSnap);

            if (currentFingerprint === parsed.fingerprint) {
              return;
            }
            setFromCache(false);
          }
        }
      } catch {}

      try {
        const fresh = await calculateStats(user);
        localStorage.setItem(CACHE_KEY(user.uid), JSON.stringify(fresh));
        setStats(fresh);
      } catch (err) {
        console.error("Stats error:", err);
      }
      setLoading(false);
      setFromCache(false);
    };

    load();
  }, [user.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const StatCard = ({ emoji, value, label }) => (
    <div style={{
      background: "var(--paper-card)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius)",
      padding: "16px",
      textAlign: "center",
    }}>
      <div style={{ fontSize: 28 }}>{emoji}</div>
      <div style={{ fontSize: 28, fontWeight: 600, color: "var(--ink)", fontFamily: "var(--font-display)", letterSpacing: -1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <>
      <header className="header">
        <button className="icon-btn" onClick={onBack}>
          <Icon name="back" size={18} />
        </button>
        <div className="header-logo" style={{ fontSize: 17 }}>Gulliver Travels</div>
        <div style={{ width: 34 }} />
      </header>

      <main className="main">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 600, color: "var(--ink)", letterSpacing: -0.5 }}>
            Travel Stats
          </div>
          {fromCache && (
            <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>cached</div>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--ink-light)" }}>
            Crunching the numbers...
          </div>
        ) : stats ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              <StatCard emoji="✈️" value={stats.totalFlights} label="Flights taken" />
              <StatCard emoji="🌍" value={stats.countries} label="Countries visited" />
              {stats.milesCalculated && <StatCard emoji="🗺️" value={stats.totalMiles.toLocaleString()} label="Miles flown" />}
              <StatCard emoji="🏨" value={stats.totalHotelNights} label="Hotel nights" />
              <StatCard emoji="🗺" value={stats.totalTrips} label="Total trips" />
              <StatCard emoji="🍽" value={stats.totalRestaurants} label="Restaurants" />
              <StatCard emoji="🚆" value={stats.totalTrains} label="Train journeys" />
            </div>

            {(stats.topAirline || stats.topAirport || stats.busiestYear) && (
              <div style={{
                background: "var(--paper-card)", border: "1px solid var(--border)",
                borderRadius: "var(--radius)", padding: "16px", marginBottom: 16,
                display: "flex", flexDirection: "column", gap: 12,
              }}>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 600 }}>Highlights</div>
                {stats.topAirline && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "var(--ink-light)" }}>Favorite airline</span>
                    <span style={{ fontWeight: 500 }}>{stats.topAirline}</span>
                  </div>
                )}
                {stats.topAirport && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "var(--ink-light)" }}>Most visited airport</span>
                    <span style={{ fontWeight: 500 }}>{stats.topAirport[0]} ({stats.topAirport[1]}×)</span>
                  </div>
                )}
                {stats.busiestYear && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "var(--ink-light)" }}>Busiest year</span>
                    <span style={{ fontWeight: 500 }}>{stats.busiestYear[0]} ({stats.busiestYear[1]} trips)</span>
                  </div>
                )}
                {stats.cities > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "var(--ink-light)" }}>Cities with hotel stays</span>
                    <span style={{ fontWeight: 500 }}>{stats.cities}</span>
                  </div>
                )}
              </div>
            )}

            {stats.countriesList.length > 0 && (
              <div style={{
                background: "var(--paper-card)", border: "1px solid var(--border)",
                borderRadius: "var(--radius)", padding: "16px", marginBottom: 16,
              }}>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
                  Countries visited
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {stats.countriesList.map(c => (
                    <div key={c} style={{
                      fontSize: 12, background: "var(--paper-warm)",
                      border: "1px solid var(--border)", borderRadius: 4,
                      padding: "3px 8px", color: "var(--ink-mid)"
                    }}>
                      {c}
                    </div>
                  ))}
                </div>
              </div>
            )}


            {Object.keys(stats.tripsByYear).length > 1 && (
              <div style={{
                background: "var(--paper-card)", border: "1px solid var(--border)",
                borderRadius: "var(--radius)", padding: "16px",
              }}>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
                  Trips by year
                </div>
                {Object.entries(stats.tripsByYear)
                  .sort(([a],[b]) => b.localeCompare(a))
                  .map(([year, count]) => (
                  <div key={year} style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                      <span style={{ color: "var(--ink-mid)" }}>{year}</span>
                      <span style={{ fontWeight: 500 }}>{count} trip{count > 1 ? "s" : ""}</span>
                    </div>
                    <div style={{ height: 4, background: "var(--border)", borderRadius: 2 }}>
                      <div style={{
                        height: 4, background: "var(--accent)", borderRadius: 2,
                        width: `${(count / Math.max(...Object.values(stats.tripsByYear))) * 100}%`
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : null}
      </main>
    </>
  );
}
